import { Loader2, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'

export interface NadiaLoaderProps {
  message?: string
  subtitle?: string
  className?: string
  minHeight?: string
  variant?: 'card' | 'inline'
}

/**
 * Componente unificado de carga para widgets y paneles analíticos.
 * Comunica visualmente que se están extrayendo y procesando datos desde NADIA.
 */
export function NadiaLoader({
  message,
  subtitle,
  className,
  minHeight = 'min-h-[220px]',
  variant = 'card',
}: NadiaLoaderProps) {
  const { t } = useTranslation()

  const defaultMsg = message || t('loading.nadiaLoading', 'Cargando datos desde NADIA...')
  const defaultSub = subtitle || t('loading.nadiaProcessing', 'Consultando modelos analíticos de Laponia...')

  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2.5 text-xs text-muted-foreground py-2', className)}>
        <Loader2 className="size-4 animate-spin text-primary" />
        <span className="font-medium text-foreground/80">{defaultMsg}</span>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'w-full flex flex-col items-center justify-center p-6 rounded-xl border border-dashed border-primary/25 bg-muted/15 backdrop-blur-sm text-center relative overflow-hidden',
        minHeight,
        className
      )}
    >
      {/* Sutil halo polar de fondo */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center gap-3">
        <div className="relative flex items-center justify-center">
          {/* Anillo de fondo suave */}
          <div className="size-12 rounded-full border-2 border-primary/15" />
          {/* Spinner giratorio clásico de alta visibilidad */}
          <Loader2 className="size-12 animate-spin text-primary absolute stroke-[2.5]" />
          {/* Destello sutil en el núcleo central */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Sparkles className="size-3.5 text-amber-400/90 animate-pulse" />
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-center gap-1.5">
            <span className="size-1.5 rounded-full bg-primary animate-ping" />
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              NADIA Engine
            </span>
          </div>
          <p className="text-sm font-medium text-foreground">{defaultMsg}</p>
          {defaultSub ? (
            <p className="text-xs text-muted-foreground max-w-sm">{defaultSub}</p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
